import { useCallback } from "react";
import { useSelector } from "react-redux";
import { Amenity, type Lodging } from "@b2bportal/lodging-api";
import { ClientName, LodgingShopTrackingEvents } from "@hopper-b2b/types";
import { Slot, StarRating, Tags } from "@hopper-b2b/ui";
import { getEnvVariables, useEnableWallet } from "@hopper-b2b/utilities";
import { Typography } from "@material-ui/core";
import clsx from "clsx";
import { useTrackEvents } from "../../../../../../tracking";
import {
  getWalletBestOffers,
  getWalletOffersNoCredits,
} from "../../../../../wallet/reducer";
import styles from "../../LodgingCard.module.scss";
import { Amenities } from "../Amenities";
import { Features } from "../Features";
import featuresStyles from "../Features/Features.module.scss";
import { Price } from "../Price";
import { TripAdvisor } from "../TripAdvisor";

interface Props {
  currentLodging: Lodging;
  loading?: boolean;
  onlyStarRating?: boolean;
}

export function LodgingSummary({
  currentLodging,
  loading = false,
  onlyStarRating = false,
}: Props) {
  const {
    lodging,
    price,
    available,
    isFreeCancel,
    isPreferred,
    bestPromotionThisLodging,
  } = currentLodging;

  const isLloydsClient = getEnvVariables("clientName") === ClientName.LLOYDS;
  const enableWallet = useEnableWallet();
  const bestVoucher = useSelector(getWalletBestOffers);
  const vouchers = useSelector(getWalletOffersNoCredits);
  // Only show the availability offer if the user hasn't already redeemed it.
  // The wallet offers list (from Scranton) checks redemption state — if the
  // offer ID isn't in that list, the user can't use it.
  const availabilityOffer = currentLodging?.bestOffer ?? null;
  const bestOfferOverall =
    availabilityOffer && vouchers?.some((v) => v.id === availabilityOffer.id)
      ? availabilityOffer
      : null;
  const trackEvent = useTrackEvents();

  const handleOfferBannerTap = useCallback(() => {
    trackEvent(LodgingShopTrackingEvents.viewed_offer, {
      screen: "shop",
    });
  }, [trackEvent]);

  return (
    <div className="Content">
      <Tags className="Tags" available={available} isPreferred={isPreferred} />
      <div className={clsx(styles.TitleWrapper, "TitleWrapper")}>
        <Typography variant="h2" className="HotelName">
          {lodging.name}
        </Typography>
        <Slot id="lodging-card-policy" lodgingId={lodging.id} autoMarginLeft />
      </div>

      <div className={clsx(styles.GridArea, "GridArea")}>
        <div className={clsx(styles.Ratings, "Ratings")}>
          <StarRating
            starRating={lodging.starRating}
            className="ratings"
            onlyStarRating={onlyStarRating}
          />
          <TripAdvisor
            tripAdvisorReviews={lodging.tripAdvisorReviews}
            className="trip-advisor-ratings TripAdvisorRatings"
          />
        </div>
        <Features
          className={clsx(styles.Features, "Features")}
          isFreeCancel={isFreeCancel}
          amenities={lodging.amenities}
        />
        <Amenities
          className={clsx(styles.Amenities, "Amenities")}
          amenities={lodging.amenities}
        />
        {bestOfferOverall ? (
          <Slot
            id="offer-banner"
            showMore
            offer={bestOfferOverall}
            onClick={handleOfferBannerTap}
            minimal
          />
        ) : null}
        {enableWallet && !isLloydsClient ? (
          <Slot
            id="hotel-voucher-offer-banner"
            isEligible
            showMore
            minimal
            offer={bestVoucher}
            offers={vouchers}
            onClick={handleOfferBannerTap}
          />
        ) : null}
        {available ? (
          <Slot
            id="hotel-summary-price"
            className={clsx(styles.Price, "Price")}
            loading={loading}
            price={price}
            promotionAmount={bestPromotionThisLodging?.discountAmount.amount}
            component={
              <Price className={clsx(styles.Price, "Price")} price={price} />
            }
          />
        ) : null}
        <div className="additional-features">
          <Slot
            id="lodging-free-cancellation"
            isFreeCancel={isFreeCancel}
            component={
              (isFreeCancel ||
                lodging.amenities?.some(
                  ({ amenity }) =>
                    amenity === Amenity.FreeBreakfast ||
                    amenity === Amenity.BreakfastIncluded ||
                    amenity === Amenity.PaidBreakfast
                )) && (
                <div className={featuresStyles.FeaturesBadges}>
                  {isFreeCancel && (
                    <Features
                      className={clsx(styles.HiddenFeatures, "HiddenFeatures")}
                      isFreeCancel={isFreeCancel}
                    />
                  )}
                  <Features
                    className={clsx(styles.HiddenFeatures, "HiddenFeatures")}
                    amenities={lodging.amenities}
                  />
                </div>
              )
            }
          />
          <Slot id="lodging-card-cash-back" price={price} />
          <Slot id="lodging-card-wallet" />
        </div>
      </div>
    </div>
  );
}
